import { type ReactNode, useCallback, useEffect, useRef } from "react";
import styles from "./SearchModal.module.css";

export type SearchModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  query: string;
  onQueryChange: (query: string) => void;
  onSearch?: (query: string) => void;
  debounceMs?: number;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  placeholder?: string;
  searching?: boolean;
  children: ReactNode;
  hasResults?: boolean;
  resultCount?: number;
  totalCount?: number;
  idleMessage?: string;
  idleHint?: string;
  emptyMessage?: string;
  filterSlot?: ReactNode;
  footerSlot?: ReactNode;
  hideIdleFooter?: boolean;
  error?: string | null;
};

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function Spinner({ size = 20 }: { size?: number }) {
  return (
    <svg
      className={styles.spinner}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.2" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SearchModal({
  open,
  onClose,
  title,
  query,
  onQueryChange,
  onSearch,
  debounceMs = 300,
  onLoadMore,
  loadingMore,
  placeholder = "Search...",
  searching,
  children,
  hasResults,
  resultCount,
  totalCount,
  idleMessage = "Start typing to search",
  idleHint = "Results will appear as you type",
  emptyMessage,
  filterSlot,
  footerSlot,
  hideIdleFooter = false,
  error,
}: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  useEffect(() => {
    if (!onSearch || !open) return;
    if (!query.trim()) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(query), debounceMs);
    return () => clearTimeout(debounceRef.current);
  }, [query, onSearch, debounceMs, open]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleScroll = useCallback(() => {
    const el = resultsRef.current;
    if (!el || !onLoadMore || loadingMore) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) onLoadMore();
  }, [onLoadMore, loadingMore]);

  useEffect(() => {
    const el = resultsRef.current;
    if (!el || !onLoadMore) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [onLoadMore, handleScroll]);

  if (!open) return null;

  const showIdle = !searching && !query.trim() && !hasResults;
  const showEmpty = !searching && query.trim() && !hasResults;
  const showCount = !searching && (resultCount ?? 0) > 0;

  return (
    <div className={styles.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {filterSlot && <div className={styles.filterSlot}>{filterSlot}</div>}

        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchInput}
            placeholder={placeholder}
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            aria-label="Search"
          />
        </div>

        <div className={styles.results} ref={resultsRef}>
          {searching ? (
            <div className={styles.emptyState}>
              <Spinner />
              Searching...
            </div>
          ) : showIdle ? (
            <div className={styles.emptyState}>
              <span>{idleMessage}</span>
              <span className={styles.emptyHint}>{idleHint}</span>
            </div>
          ) : showEmpty ? (
            <div className={styles.emptyState}>
              <span>{emptyMessage ?? `No results found for "${query}"`}</span>
              <span className={styles.emptyHint}>Try a different search term</span>
            </div>
          ) : (
            <>
              {children}
              {loadingMore && (
                <div className={styles.loadingMore}>
                  <Spinner size={16} />
                  Loading more...
                </div>
              )}
            </>
          )}
        </div>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        {footerSlot && <div className={styles.footerSlot}>{footerSlot}</div>}

        {(showCount || !hideIdleFooter) && (
          <div className={styles.footer}>
            {showCount ? (
              <span>
                Showing {resultCount}
                {totalCount && totalCount > (resultCount ?? 0) ? ` of ${totalCount}` : ""} result
                {(resultCount ?? 0) !== 1 ? "s" : ""}
              </span>
            ) : (
              <span>Search results will appear above</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { styles as searchModalStyles };
